import { writable } from 'svelte/store';
import type { ChatMessage } from '../ChatWidget.svelte';

const DEFAULT_EXPIRATION_MS = 1000 * 60 * 60; // 1 hour
const DEFAULT_STORAGE_KEY = 'svelte-chat-widget::messages';

type PersistMode = 'memory' | 'local';

type ChatHistoryOptions = {
  initialMessages: ChatMessage[];
  persist?: PersistMode;
  expirationMs?: number;
  storageKey?: string;
};

type StoredPayload = {
  messages: ChatMessage[];
  updatedAt: number;
};

type PersistenceAdapter = {
  load: () => StoredPayload | null;
  save: (messages: ChatMessage[]) => void;
  clear: () => void;
};

export type ChatHistoryStore = ReturnType<typeof createChatHistory>;

export function createChatHistory({
  initialMessages,
  persist = 'memory',
  expirationMs = DEFAULT_EXPIRATION_MS,
  storageKey = DEFAULT_STORAGE_KEY
}: ChatHistoryOptions) {
  const adapter = createPersistenceAdapter(persist, storageKey, expirationMs, initialMessages);
  const boot = adapter.load() ?? { messages: initialMessages, updatedAt: Date.now() };
  const store = writable<ChatMessage[]>(boot.messages);

  let snapshot = boot.messages;
  store.subscribe((value) => {
    snapshot = value;
    adapter.save(value);
  });

  function setMessages(next: ChatMessage[]) {
    if (snapshot === next) return;
    store.set(next);
  }

  function append(message: ChatMessage) {
    store.update((current) => [...current, message]);
  }

  function reset() {
    adapter.clear();
    store.set(initialMessages);
  }

  function getSnapshot() {
    return snapshot;
  }

  return {
    subscribe: store.subscribe,
    set: setMessages,
    append,
    reset,
    getSnapshot
  };
}

function createPersistenceAdapter(
  mode: PersistMode,
  storageKey: string,
  expirationMs: number,
  fallback: ChatMessage[]
): PersistenceAdapter {
  if (mode !== 'local' || typeof window === 'undefined') {
    return createMemoryAdapter();
  }

  return {
    load: () => {
      try {
        const raw = window.localStorage.getItem(storageKey);
        if (!raw) return null;
        const payload = JSON.parse(raw) as StoredPayload;
        if (!payload?.messages) return null;
        if (Date.now() - payload.updatedAt > expirationMs) {
          window.localStorage.removeItem(storageKey);
          return null;
        }
        return payload;
      } catch {
        window.localStorage.removeItem(storageKey);
        return null;
      }
    },
    save: (messages) => {
      try {
        const payload: StoredPayload = {
          messages,
          updatedAt: Date.now()
        };
        window.localStorage.setItem(storageKey, JSON.stringify(payload));
      } catch {
        // Ignore storage failures (private browsing, quota, etc.)
      }
    },
    clear: () => {
      try {
        window.localStorage.removeItem(storageKey);
      } catch {
        // Ignore
      }
    }
  };
}

function createMemoryAdapter(): PersistenceAdapter {
  let cache: StoredPayload | null = null;

  return {
    load: () => cache,
    save: (messages) => {
      cache = {
        messages,
        updatedAt: Date.now()
      };
    },
    clear: () => {
      cache = null;
    }
  };
}
